import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { ShoppingCart, CreditCard, Package, TrendingUp, Award, Shield, Headset, Globe, ChevronDown, MessageSquare, Zap, Smartphone, Laptop, Store, HeartPulse, Building2, Truck, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useState } from "react";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

const FAQ = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        className="flex justify-between items-center w-full text-left py-3 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-lg text-gray-900">{question}</span>
        <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="mt-2 text-gray-600">
          {answer}
        </div>
      )}
    </div>
  );
};

export default function ECommerce() {
  const { openDrawer } = useQuoteDrawer();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const features = [
    {
      title: "Custom eCommerce Platforms",
      description: "Tailored online stores built to match your brand and business requirements.",
      icon: <ShoppingCart className="h-8 w-8 text-blue-600" />
    },
    {
      title: "Payment Gateway Integration",
      description: "Secure payment processing with multiple payment options for customers.",
      icon: <CreditCard className="h-8 w-8 text-green-600" />
    },
    {
      title: "Inventory Management",
      description: "Advanced inventory tracking and management systems for efficient operations.",
      icon: <Package className="h-8 w-8 text-blue-600" />
    },
    {
      title: "Sales Analytics",
      description: "Comprehensive reporting and analytics to track performance and growth.",
      icon: <TrendingUp className="h-8 w-8 text-purple-600" />
    }
  ];

  const platforms = [
    { name: "Shopify", icon: "fas fa-shopping-bag" },
    { name: "WooCommerce", icon: "fab fa-wordpress" },
    { name: "Magento", icon: "fas fa-store" },
    { name: "Custom Build", icon: "fas fa-code" },
    { name: "BigCommerce", icon: "fas fa-shopping-cart" },
    { name: "PrestaShop", icon: "fas fa-box" }
  ];

  const whyChooseUs = [
    {
      icon: <Award className="h-8 w-8" />,
      title: "Proven Track Record",
      description: "Successful eCommerce projects across various industries"
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Secure & Reliable",
      description: "Enterprise-grade security and 99.9% uptime guarantee"
    },
    {
      icon: <Headset className="h-8 w-8" />,
      title: "Dedicated Support",
      description: "24/7 technical support for your online store"
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Global Reach",
      description: "Multi-currency and multi-language support"
    }
  ];

  const industries = [
    {
      name: "Fashion & Apparel",
      icon: <ShoppingCart className="h-6 w-6" />,
      description: "Trendy online clothing stores"
    },
    {
      name: "Electronics",
      icon: <Smartphone className="h-6 w-6" />,
      description: "High-tech gadget marketplaces"
    },
    {
      name: "Home & Living",
      icon: <Laptop className="h-6 w-6" />,
      description: "Furniture and home decor"
    },
    {
      name: "Health & Beauty",
      icon: <HeartPulse className="h-6 w-6" />,
      description: "Wellness and cosmetics"
    },
    {
      name: "B2B Wholesale",
      icon: <Building2 className="h-6 w-6" />,
      description: "Business-to-business commerce"
    },
    {
      name: "Food & Grocery",
      icon: <Truck className="h-6 w-6" />,
      description: "Online supermarkets"
    }
  ];

  const testimonials = [
    {
      quote: "Our online sales increased by 300% within 3 months of launching our new eCommerce site. The team's expertise was invaluable.",
      author: "Ahmed Al Maktoum",
      role: "CEO, UrbanStyle Fashion"
    },
    {
      quote: "The custom features they built for our B2B portal have streamlined our operations and improved customer experience significantly.",
      author: "Fatima Al Qasimi",
      role: "Operations Director, TechGulf"
    }
  ];

  const faqs = [
    {
      question: "How much does an eCommerce website cost?",
      answer: "Costs vary based on features and complexity, typically ranging from $5,000 for basic stores to $50,000+ for custom enterprise solutions."
    },
    {
      question: "How long does it take to build an eCommerce website?",
      answer: "Basic stores can launch in 4-6 weeks, while custom solutions may take 3-6 months depending on requirements."
    },
    {
      question: "Do you provide training for managing the online store?",
      answer: "Yes, we provide comprehensive training and documentation to help you manage your store effectively."
    },
    {
      question: "Can you migrate my existing store to a new platform?",
      answer: "Absolutely! We offer seamless migration services with zero data loss."
    }
  ];

  const ecommerceFeatures = [
    {
      title: "Mobile-Responsive Design",
      description: "Fully responsive stores that work perfectly on all devices",
      icon: <Smartphone className="h-6 w-6" />
    },
    {
      title: "SEO Optimization",
      description: "Built-in SEO tools to help you rank higher in search results",
      icon: <TrendingUp className="h-6 w-6" />
    },
    {
      title: "Multi-Payment Gateways",
      description: "Support for credit cards, PayPal, Apple Pay, and more",
      icon: <CreditCard className="h-6 w-6" />
    },
    {
      title: "Advanced Analytics",
      description: "Track sales, customer behavior, and inventory in real-time",
      icon: <TrendingUp className="h-6 w-6" />
    },
    {
      title: "Inventory Management",
      description: "Automated stock control and low stock alerts",
      icon: <Package className="h-6 w-6" />
    },
    {
      title: "Multi-Currency Support",
      description: "Sell globally with automatic currency conversion",
      icon: <Globe className="h-6 w-6" />
    },
    {
      title: "Customer Accounts",
      description: "Allow customers to track orders and save payment methods",
      icon: <Headset className="h-6 w-6" />
    },
    {
      title: "One-Click Checkout",
      description: "Reduce cart abandonment with streamlined checkout",
      icon: <ShoppingCart className="h-6 w-6" />
    }
  ];

  return (
    <>
      <SEO
        title="E-Commerce Development UAE | ZeeXSoft"
        description="Professional e-commerce solutions in UAE. Build high-converting online stores with secure payment gateways since 2017."
        keywords="e-commerce uae, online store dubai, shopify development sharjah"
      />

      <main className="bg-white text-gray-900">
        <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Powerful <span className="text-blue-600">E-Commerce</span> Solutions
            </h1>
            <p className="text-xl max-w-3xl mx-auto mb-8">
              Custom online stores designed to maximize conversions and sales
            </p>
          </div>
        </section>

        {/* Hero Section */}
        <section className="relative py-32 bg-gradient-to-br from-blue-50 to-purple-50 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900">
                Transform Your <span className="text-blue-600">Online Business</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-lg">
                Professional eCommerce development services to grow your sales and streamline operations
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg"
                  onClick={openDrawer}
                >
                  Get a Free Quote
                </Button>
                <Link href="/portfolio">
                  <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg">
                    View Our Portfolio
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Our eCommerce Services</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Comprehensive solutions tailored to your business needs</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-50 rounded-xl p-8 hover:shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  <div className="w-16 h-16 rounded-lg bg-blue-100 flex items-center justify-center mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Why Choose Our eCommerce Solutions?</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">We build online stores that drive sales and grow your business</p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl p-8 hover:shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  <div className="w-16 h-16 rounded-lg bg-blue-100 flex items-center justify-center mb-6">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Platforms Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">eCommerce Platforms We Work With</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">We're experts in all major eCommerce platforms and custom solutions</p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {platforms.map((platform, index) => (
                <motion.div
                  key={platform.name}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-gray-50 rounded-xl p-6 hover:shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  <i className={`${platform.icon} text-4xl text-blue-600 mb-4`}></i>
                  <p className="font-orbitron text-sm mt-2">{platform.name}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* E-commerce Features Grid */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Comprehensive eCommerce Features</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Everything you need to run a successful online store</p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {ecommerceFeatures.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-lg p-6 hover:shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries We Serve */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Industries We Serve</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Custom eCommerce solutions for every business type</p>
            </motion.div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {industries.map((industry, index) => (
                <motion.div
                  key={industry.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-50 rounded-xl p-6 hover:shadow-lg hover:shadow-blue-500/20 transition-all flex items-start"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mr-4 flex-shrink-0">
                    {industry.icon}
                  </div>
                  <div>
                    <h3 className="font-orbitron font-semibold text-lg mb-1">{industry.name}</h3>
                    <p className="text-gray-600 text-sm">{industry.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Proven eCommerce Results</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Our solutions consistently deliver exceptional performance</p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { metric: "250%", label: "Average Sales Increase", icon: <TrendingUp className="h-8 w-8 mx-auto mb-3 text-blue-600" /> },
                { metric: "40%", label: "Cart Conversion Rate", icon: <ShoppingCart className="h-8 w-8 mx-auto mb-3 text-blue-600" /> },
                { metric: "99.9%", label: "Uptime Guarantee", icon: <Shield className="h-8 w-8 mx-auto mb-3 text-blue-600" /> },
                { metric: "<2s", label: "Avg. Load Time", icon: <Zap className="h-8 w-8 mx-auto mb-3 text-blue-600" /> }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl p-8 hover:shadow-lg hover:shadow-blue-500/20 transition-all text-center"
                >
                  {stat.icon}
                  <div className="font-orbitron font-bold text-3xl md:text-4xl text-blue-600 mb-2">{stat.metric}</div>
                  <div className="text-gray-600 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">What Our Clients Say</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Success stories from businesses we've helped grow</p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-50 rounded-xl p-8 hover:shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  <div className="flex items-center mb-6">
                    <div className="text-yellow-400 mr-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5" />
                      ))}
                    </div>
                  </div>
                  <p className="text-lg italic mb-6">"{testimonial.quote}"</p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold mr-4">
                      {testimonial.author.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-semibold">{testimonial.author}</h4>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Client Success Stories</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">What our clients say about our eCommerce solutions</p>
            </motion.div>

            <div className="max-w-4xl mx-auto relative">
              <motion.div 
                key={currentTestimonial}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-xl p-8 shadow-lg"
              >
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-xl text-gray-600 mb-6">"{testimonials[currentTestimonial].quote}"</p>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{testimonials[currentTestimonial].author}</p>
                  <p className="text-blue-600">{testimonials[currentTestimonial].role}</p>
                </div>
              </motion.div>

              <div className="flex justify-center mt-8 gap-4">
                <button 
                  onClick={() => setCurrentTestimonial(prev => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                  className="p-2 rounded-full bg-gray-50 text-gray-600 hover:bg-blue-500 hover:text-white transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button 
                  onClick={() => setCurrentTestimonial(prev => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                  className="p-2 rounded-full bg-gray-50 text-gray-600 hover:bg-blue-500 hover:text-white transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6 max-w-4xl">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-blue-600 mb-6">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything you need to know about our eCommerce development services</p>
            </motion.div>
            
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <FAQ key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="E-Commerce" 
              services={[
                {
                  title: "Mobile App Development",
                  description: "Build engaging mobile shopping experiences for iOS and Android.",
                  icon: <Smartphone className="h-8 w-8" />
                },
                {
                  title: "Web Design & Development",
                  description: "Create responsive and optimized eCommerce websites.",
                  icon: <Laptop className="h-8 w-8" />
                },
                {
                  title: "Digital Marketing",
                  description: "Drive traffic and sales with targeted marketing campaigns.",
                  icon: <ShoppingCart className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        <WebDesignLocations />

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="container mx-auto px-6 text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Ready to Grow Your Online Business?</h2>
              <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
                Let's build an eCommerce platform that converts visitors into customers and scales with your business
              </p>
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
                onClick={openDrawer}
              >
                Get Started Today
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
